import React, { useRef } from "react";

const MOVE_THRESHOLD = 4;

// Change this if you want the cursor faster/slower.
const SENSITIVITY = 1.5;

const GRID_ROWS = 6;
const GRID_COLS = 6;

export default function TouchpadView({ send, mode }) {
  // Keep refs in sync so the handlers always read the latest props.
  const modeRef = useRef(mode);
  modeRef.current = mode;

  const sendRef = useRef(send);
  sendRef.current = send;

  const activePointerId = useRef(null);

  const startX = useRef(0);
  const startY = useRef(0);

  const lastDx = useRef(0);
  const lastDy = useRef(0);

  const totalDx = useRef(0);
  const totalDy = useRef(0);

  const isMoving = useRef(false);

  const resetGesture = () => {
    activePointerId.current = null;

    lastDx.current = 0;
    lastDy.current = 0;

    totalDx.current = 0;
    totalDy.current = 0;

    isMoving.current = false;
  };

  // ----------------------------------------------------------
  // POINTER HANDLERS (replace PanResponder)
  // ----------------------------------------------------------

  const handlePointerDown = (event) => {
    // Ignore a second finger / button while a gesture is active
    if (activePointerId.current !== null) return;

    activePointerId.current = event.pointerId;

    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {}

    startX.current = event.clientX;
    startY.current = event.clientY;

    lastDx.current = 0;
    lastDy.current = 0;

    totalDx.current = 0;
    totalDy.current = 0;

    isMoving.current = false;

    sendRef.current("touchStart", { mode: modeRef.current });
  };

  const handlePointerMove = (event) => {
    if (event.pointerId !== activePointerId.current) return;

    const currentDx = event.clientX - startX.current;
    const currentDy = event.clientY - startY.current;

    const deltaX = currentDx - lastDx.current;
    const deltaY = currentDy - lastDy.current;

    lastDx.current = currentDx;
    lastDy.current = currentDy;

    totalDx.current += deltaX;
    totalDy.current += deltaY;

    if (
      Math.abs(totalDx.current) > MOVE_THRESHOLD ||
      Math.abs(totalDy.current) > MOVE_THRESHOLD
    ) {
      isMoving.current = true;
    }

    if (deltaX === 0 && deltaY === 0) {
      return;
    }

    sendRef.current("touchMove", {
      dx: deltaX * SENSITIVITY,
      dy: deltaY * SENSITIVITY,
      mode: modeRef.current,
    });
  };

  const handlePointerUp = (event) => {
    if (event.pointerId !== activePointerId.current) return;

    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {}

    if (isMoving.current) {
      sendRef.current("touchEnd", { mode: modeRef.current });
    } else {
      sendRef.current("touchTap", { mode: modeRef.current });
    }

    resetGesture();
  };

  const handlePointerCancel = (event) => {
    if (event.pointerId !== activePointerId.current) return;

    if (isMoving.current) {
      sendRef.current("touchEnd", { mode: modeRef.current });
    }

    resetGesture();
  };

  // ----------------------------------------------------------
  // UI
  // ----------------------------------------------------------

  return (
    <div className="tp-wrapper">
      <div className="tp-heading">
        {mode === "mouse" ? "Mouse Control" : "Drag and Rotate Model"}
      </div>

      <div
        className="tp-touchpad"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
      >
        {/* Grid paper background */}
        <div className="tp-grid-overlay">
          {Array.from({ length: GRID_ROWS - 1 }).map((_, i) => (
            <div
              key={`row-${i}`}
              className="tp-grid-line-h"
              style={{ top: `${((i + 1) / GRID_ROWS) * 100}%` }}
            />
          ))}
          {Array.from({ length: GRID_COLS - 1 }).map((_, i) => (
            <div
              key={`col-${i}`}
              className="tp-grid-line-v"
              style={{ left: `${((i + 1) / GRID_COLS) * 100}%` }}
            />
          ))}
        </div>

        <div className="tp-center-indicator">
          <span className="tp-icon">{mode === "mouse" ? "🖱️" : "🎲"}</span>
          {mode === "rotate" && <span className="tp-rotate-arrow">↻</span>}
        </div>
      </div>
    </div>
  );
}
